import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import store, { IAppState } from "../../state/Store";
import Datastore from "../../communication/Datastore";
import EmailServer from "../../types/core/EmailServer";
import { REHYDRATE } from "redux-persist";

// Define the type for our state store entry
type EmailServerState = {
    emailServer: EmailServer;
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: EmailServerState = {
    emailServer: null,
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const emailServerSlice = createSlice({
    name: "emailServers",
    initialState: initialState,
    reducers: {
        updateEmailServerDetails: (
            state,
            action: PayloadAction<EmailServer>,
        ) => {
            state.emailServer = new EmailServer(action.payload);
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const server = rawData?.payload?.emailServers?.emailServer
                    ? new EmailServer(
                          rawData?.payload?.emailServers.emailServer,
                      )
                    : null;

                return {
                    emailServer: server,
                    status: !server
                        ? "idle"
                        : rawData?.payload?.emailServers?.status,
                    error: null,
                };
            })
            .addCase(fetchEmailServer.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchEmailServer.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.emailServer = new EmailServer(action.payload);
            })
            .addCase(fetchEmailServer.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateEmailServer.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateEmailServer.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.emailServer = new EmailServer(action.payload);
            })
            .addCase(updateEmailServer.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { updateEmailServerDetails } = emailServerSlice.actions;

export const initialiseEmailServerDetails = createAsyncThunk(
    "emailServers/initialiseEmailServerDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchEmailServer()),
);

// This is a thunk, they are used to load the state
const fetchEmailServer = createAsyncThunk(
    "emailServers/fetchEmailServer",
    () => Datastore.getType<EmailServer>("api/EmailServers"),
    {
        condition: (_, api) =>
            store.getState().db.emailServers.status === "idle",
    },
);
export const updateEmailServer = createAsyncThunk(
    "emailServers/updateEmailServer",
    async (value: EmailServer, thunkAPI) => {
        let result: EmailServer = null;

        value.errorMessage = "";
        value.sendFromAddresses.forEach(item => (item.errorMessage = ""));

        if (value.id > 0)
            result = await Datastore.putType<EmailServer>(
                "api/EmailServers",
                value,
            );
        else
            result = await Datastore.postType<EmailServer>(
                "api/EmailServers",
                value,
            );

        await Datastore.get("api/EmailServers/Test");
        setTimeout(checkTestEmailServerDetails, 1000);

        result.serverTesting = true;

        return result;
    },
);
const checkTestEmailServerDetails = async () => {
    let result = await Datastore.getType<EmailServer>("api/EmailServers");
    const testing =
        (result.tested !== true && result.tested !== false) ||
        result.sendFromAddresses.find(
            item => item.tested !== true && item.tested !== false,
        ) !== undefined;

    if (testing) {
        result.serverTesting = true;
        setTimeout(checkTestEmailServerDetails, 2000);
    } else {
        result.serverTesting = false;
    }

    store.dispatch(updateEmailServerDetails(result));
};

// We have to export the reducer as this is added to the state store
export default emailServerSlice.reducer;
